// src/pages/PostDetail/index.jsx
import React, { useState, useEffect } from 'react';
import { Card, Typography, Tag, Avatar, Divider, Button, Input, List, message, Spin } from 'antd';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  CalendarOutlined, 
  EyeOutlined, 
  LikeOutlined, 
  MessageOutlined,
  UserOutlined,
  ArrowLeftOutlined,
  SendOutlined 
} from '@ant-design/icons';
import { fakeApi } from '../../api/fakeApi';

const { Title, Paragraph } = Typography;
const { TextArea } = Input;

const PostDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [commenting, setCommenting] = useState(false);
  
  // 检查是否从归档页面跳转过来
  const fromArchive = location.state?.fromArchive || false;

  useEffect(() => {
    const postId = parseInt(id);
    if (isNaN(postId)) {
      message.error('文章ID无效');
      navigate('/postcard');
      return;
    }
    fetchPostDetails(postId);
  }, [id]);

  const fetchPostDetails = async (postId) => {
    setLoading(true);
    try {
      // 获取文章详情
      const postResponse = await fakeApi.posts.getPost(postId);
      if (postResponse.success) {
        setPost(postResponse.data);
        
        // 获取评论
        const commentsResponse = await fakeApi.comments.getPostComments(postId);
        if (commentsResponse.success) {
          setComments(commentsResponse.data);
        }
      } else {
        message.error(postResponse.message || '文章不存在');
        navigate('/postcard');
      }
    } catch (error) {
      message.error('加载失败');
      console.error('加载文章详情失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (fromArchive) {
      navigate('/archive');
    } else {
      navigate('/postcard');
    }
  };

  const handleLike = async () => {
      const user = localStorage.getItem('user');
      if (!user) {
        message.warning('请先登录再点赞');
        navigate('/auth');
        return;
      }
    try {
      const postId = parseInt(id);
      const response = await fakeApi.posts.likePost(postId);
      if (response.success) {
        setPost(prev => ({
          ...prev,
          likes: response.data.likes
        }));
        message.success('点赞成功');
      }
    } catch (error) {
      console.error('点赞失败:', error);
      message.error('点赞失败');
    }
  };

  const handleAddComment = async () => {
    const user = localStorage.getItem('user');
    if (!user) {
      message.warning('请先登录再评论');
      navigate('/auth');
      return;
    }
    if (!commentText.trim()) {
      message.warning('请输入评论内容');
      return;
    }

    setCommenting(true);
    try {
      const postId = parseInt(id);
      const user = JSON.parse(localStorage.getItem('user') || '{"name": "匿名用户"}');
      
      const response = await fakeApi.comments.addComment(postId, {
        author: user.name,
        content: commentText
      });

      if (response.success) {
        message.success('评论发布成功');
        setCommentText('');
        // 刷新评论列表
        const commentsResponse = await fakeApi.comments.getPostComments(postId);
        if (commentsResponse.success) {
          setComments(commentsResponse.data);
          // 更新文章评论数
          setPost(prev => ({
            ...prev,
            comments: (prev.comments || 0) + 1
          }));
        }
      } else {
        message.error(response.message || '评论发布失败');
      }
    } catch (error) {
      message.error('评论发布失败');
      console.error('发布评论失败:', error);
    } finally {
      setCommenting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>加载中...</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div style={{ padding: 24 }}>
        <Card>
          <Title level={3}>文章不存在</Title>
          <Button onClick={() => navigate('/postcard')}>返回文章列表</Button>
        </Card>
      </div>
    );
  }

  // 确保所有要渲染的数据都是字符串或React元素
  const safeAuthor = typeof post.author === 'string' ? post.author : '匿名用户';
  const safeAuthorAvatar = typeof post.authorAvatar === 'string' ? post.authorAvatar : 
                         (typeof post.author === 'string' ? post.author.charAt(0) : '匿');
  const safeDate = typeof post.date === 'string' ? post.date : '未知日期';
  const safeTitle = typeof post.title === 'string' ? post.title : '无标题';
  const safeContent = typeof post.content === 'string' ? post.content : '<p>暂无内容</p>';
  const safeTags = Array.isArray(post.tags) ? post.tags : [];

  return (
    <div style={{ padding: 24 }}>
      <Button 
        icon={<ArrowLeftOutlined />} 
        onClick={handleBack}
        style={{ marginBottom: 16 }}
      >
        {fromArchive ? '返回归档' : '返回文章列表'}
      </Button>
      
      <Card>
        <Title level={2}>{safeTitle}</Title>
        
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
          <Avatar 
            size="large" 
            style={{ 
              backgroundColor: '#1890ff', 
              marginRight: 12
            }}
          >
            {safeAuthorAvatar}
          </Avatar>
          <div>
            <div style={{ fontWeight: 'bold' }}>{safeAuthor}</div>
            <div style={{ color: '#999', fontSize: 14 }}>
              <CalendarOutlined style={{ marginRight: 4 }} />
              {safeDate}
            </div>
          </div>
          
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 16, alignItems: 'center' }}>
            <span style={{ color: '#666' }}>
              <EyeOutlined style={{ marginRight: 4 }} />
              {typeof post.views === 'number' ? post.views : 0}
            </span>
            <Button 
              type="text" 
              icon={<LikeOutlined />} 
              onClick={handleLike}
              style={{ color: '#666' }}
            >
              {typeof post.likes === 'number' ? post.likes : 0}
            </Button>
            <span style={{ color: '#666' }}>
              <MessageOutlined style={{ marginRight: 4 }} />
              {typeof post.comments === 'number' ? post.comments : 0}
            </span>
          </div>
        </div>
        
        <div style={{ marginBottom: 24 }}>
          {safeTags.map(tag => (
            <Tag key={tag} color="blue" style={{ marginRight: 8 }}>
              {typeof tag === 'string' ? tag : '标签'}
            </Tag>
          ))}
        </div>
        
        <Divider />
        
        <div 
          style={{ 
            fontSize: 16, 
            lineHeight: 1.8,
            marginBottom: 40 
          }}
          dangerouslySetInnerHTML={{ __html: safeContent }}
        />
        
        <Divider />
        
        {/* 评论区域 */}
        <div style={{ marginTop: 40 }}>
          <Title level={4}>评论 ({comments.length})</Title>
          
          {comments.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
              暂无评论，快来发表第一条评论吧！
            </div>
          ) : (
            <List
              dataSource={comments}
              renderItem={comment => {
                // 确保评论数据是有效的
                const safeCommentAuthor = typeof comment.author === 'string' ? comment.author : '匿名用户';
                const safeCommentContent = typeof comment.content === 'string' ? comment.content : '无内容';
                const safeCommentDate = typeof comment.date === 'string' ? 
                  new Date(comment.date).toLocaleString() : '未知时间';
                
                return (
                  <List.Item
                    style={{ 
                      borderBottom: '1px solid #f0f0f0',
                      padding: '16px 0'
                    }}
                  >
                    <div style={{ display: 'flex', width: '100%' }}>
                      <Avatar 
                        size="default" 
                        style={{ 
                          backgroundColor: '#52c41a',
                          marginRight: 12,
                          flexShrink: 0
                        }}
                      >
                        {safeCommentAuthor.charAt(0)}
                      </Avatar>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                          <div>
                            <span style={{ fontWeight: 'bold', marginRight: 8 }}>
                              {safeCommentAuthor}
                            </span>
                            <span style={{ fontSize: '12px', color: '#999' }}>
                              {safeCommentDate}
                            </span>
                          </div>
                        </div>
                        <Paragraph style={{ margin: 0 }}>
                          {safeCommentContent}
                        </Paragraph>
                      </div>
                    </div>
                  </List.Item>
                );
              }}
              style={{ marginBottom: 24 }}
            />
          )}
          
          {/* 发表评论区域 */}
          <div style={{ marginTop: 24 }}>
            <Title level={5}>发表评论</Title>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <Avatar size="default" icon={<UserOutlined />} />
              <div style={{ flex: 1 }}>
                <TextArea 
                  rows={3} 
                  placeholder="写下你的评论..." 
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  style={{ marginBottom: 12 }}
                />
                <Button 
                  type="primary" 
                  icon={<SendOutlined />}
                  onClick={handleAddComment}
                  loading={commenting}
                >
                  发表评论
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default PostDetail;